import { tryOrDefault } from '@pkg/shared/utils'
import { createServerOnlyFn } from '@tanstack/react-start'

export type CallerInfo =
  | {
      file?: string
      line?: number
      column?: number
      fn?: string
    }
  | undefined

// Resolve Node's `findSourceMap` server-side only. createServerOnlyFn lets the TanStack
// Start compiler strip the `node:module` import (and the loader call) from the client/PWA
// bundle, replacing it with a stub. On the server we await it once at module load and
// cache the function so mapFrame() can stay sync.
type SourceMapEntry = {
  originalSource?: string
  originalLine: number
  originalColumn: number
  name?: string
}
type SourceMap = { findEntry(line: number, column: number): SourceMapEntry | undefined }
type FindSourceMap = (url: string) => SourceMap | undefined

const loadFindSourceMap = createServerOnlyFn(
  (): Promise<FindSourceMap> => import('node:module').then(m => m.findSourceMap as unknown as FindSourceMap),
)

const findSourceMap = await tryOrDefault(() => loadFindSourceMap())

// Translate a bundled (file, line, col) to the original source via the inline sourcemap.
// Returns the original tuple when no sourcemap exists or the lookup misses. Node's
// --enable-source-maps would do this on `err.stack` automatically, but doesn't reliably
// translate URLs with query strings like `real.mjs?t=12345`. Doing it ourselves works
// regardless of how the script was loaded.
const mapFrame = (file: string, line: number, column: number) => {
  // Strip any cache-bust query before looking up the sourcemap.
  const cleanFile = file.replace(/\?[^#]*$/, '')
  if (!findSourceMap) return { file: cleanFile, line, column, fn: undefined as string | undefined }
  try {
    const sm = findSourceMap(cleanFile)
    const entry = sm?.findEntry(line - 1, column - 1)
    if (entry?.originalSource) {
      return {
        file: entry.originalSource.replace(/^file:\/\//, ''),
        line: entry.originalLine + 1,
        column: entry.originalColumn + 1,
        fn: entry.name,
      }
    }
  } catch {
    // findSourceMap throws for scripts not loaded via Node's loader (e.g. eval).
  }
  return { file: cleanFile, line, column, fn: undefined as string | undefined }
}

export function getCaller(): CallerInfo {
  try {
    const err = new Error()
    // Prefer captureStackTrace when available (Node/V8); also helps exclude this function.
    ;(Error as any).captureStackTrace?.(err, getCaller)

    const raw = String(err.stack ?? '')
    if (!raw) return undefined

    const lines = raw.split('\n')
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('at ')) continue

      // Typical V8 formats:
      // - at fn (file:line:col)
      // - at file:line:col
      const match = /^at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/.exec(trimmed)
      if (!match) continue

      const rawFn = match[1]
      const rawFile = match[2]
      const rawLineNo = Number(match[3])
      const rawColNo = Number(match[4])
      if (!rawFile || !Number.isFinite(rawLineNo) || !Number.isFinite(rawColNo)) continue

      const mapped = mapFrame(rawFile, rawLineNo, rawColNo)
      const file = mapped.file
      const fn = mapped.fn ?? rawFn

      // Skip internal frames and our own logger wrapper. Run this AFTER sourcemap
      // translation — the bundled file is always `real.mjs`, so unmapped paths would
      // never match the logger/pino skip patterns.
      if (
        file?.includes('/packages/shared/src/logger.ts') ||
        file?.includes('packages/shared/src/logger.ts') ||
        file?.includes('/packages/shared/src/logger/') ||
        file?.includes('/node_modules/') ||
        file?.startsWith('node:') ||
        file?.includes('node:internal') ||
        file?.includes('internal/') ||
        file?.includes('pino')
      ) {
        continue
      }

      return {
        file,
        line: mapped.line,
        column: mapped.column,
        fn,
      }
    }

    return undefined
  } catch {
    return undefined
  }
}
